import { useCallback, useEffect, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import { toast } from '@/components';
import { useGridMultiSelection, useOpenInNewWindow } from '@/hooks';
import { usePagesStateStore } from '@/stores';
import { useScrollRestore } from '@/hooks';
import { revalidateByCacheKeyPrefix } from '@/utils/swr';
import type { PaginatedResponse } from '@/types/responses';
import { StatusFilter } from './types';
import type { DirectoryGridConfig, ModalMode } from './types';
import { useDirectoryFilters } from './hooks/useDirectoryFilters';
import { useDirectoryEditModal } from './hooks/useDirectoryEditModal';
import { useDirectoryDeleteFlow } from './hooks/useDirectoryDeleteFlow';

const PAGE_SIZE = 16;

export function useDirectoryGrid<
    TItem extends { id: string },
    TPayload = Record<string, unknown>,
>(config: DirectoryGridConfig<TItem, TPayload>) {
    const { openUrl } = useOpenInNewWindow();
    const { getPageState, setPageState, resetPageState } = usePagesStateStore();

    const [scrollContainer, setScrollContainer] =
        useState<HTMLDivElement | null>(null);
    const [isUpdating, setIsUpdating] = useState(false);

    const filters = useDirectoryFilters(config.defaultSort);
    const orderBy = config.orderByMap[filters.sortOption];

    const cacheKey = [
        config.cacheKeyPrefix,
        filters.searchQuery ? `search-${filters.searchQuery}` : '',
        filters.statusFilter !== StatusFilter.ALL
            ? `status-${filters.statusFilter}`
            : '',
        filters.selectedTags.length
            ? `tags-${filters.selectedTags.join(',')}`
            : '',
        filters.selectedVehicleTypes.length
            ? `vehicleTypes-${filters.selectedVehicleTypes.join(',')}`
            : '',
        `sort-${filters.sortOption}`,
    ]
        .filter(Boolean)
        .join('-');

    const getKey = useCallback(
        (
            pageIndex: number,
            previousPageData: PaginatedResponse<TItem> | null,
        ): [string, number] | null => {
            if (previousPageData && !previousPageData.items.length) return null;
            return [cacheKey, pageIndex];
        },
        [cacheKey],
    );

    const fetcher = useCallback(
        async ([, pageIndex]: [string, number]): Promise<
            PaginatedResponse<TItem>
        > =>
            config.fetchItems({
                page: pageIndex + 1,
                size: PAGE_SIZE,
                search: filters.searchQuery || undefined,
                known: filters.knownParam,
                tags: filters.selectedTags.length
                    ? filters.selectedTags
                    : undefined,
                vehicleTypes: filters.selectedVehicleTypes.length
                    ? filters.selectedVehicleTypes
                    : undefined,
                orderBy,
            }),
        [
            config,
            filters.searchQuery,
            filters.knownParam,
            filters.selectedTags,
            filters.selectedVehicleTypes,
            orderBy,
        ],
    );

    const { data, error, size, setSize, isLoading, isValidating, mutate } =
        useSWRInfinite<PaginatedResponse<TItem>>(getKey, fetcher, {
            revalidateFirstPage: false,
            revalidateOnFocus: false,
        });

    const {
        data: statsData,
        error: statsError,
        mutate: mutateStats,
    } = useSWR(config.statsCacheKey, () => config.fetchStats(), {
        revalidateOnFocus: false,
    });
    const {
        data: tagOptions = [],
        error: tagsError,
        mutate: mutateTags,
    } = useSWR(
        config.fetchTags && config.tagsCacheKey ? config.tagsCacheKey : null,
        () => config.fetchTags?.() ?? Promise.resolve([]),
        { revalidateOnFocus: false },
    );

    const resetPagination = useCallback(() => {
        setSize(1);
        resetPageState(config.pageKey);
    }, [setSize, resetPageState, config.pageKey]);

    const [sizeRestored, setSizeRestored] = useState(false);
    useEffect(() => {
        if (sizeRestored) return;
        const { size: storedSize } = getPageState(config.pageKey);
        if (storedSize > 1) {
            setSizeRestored(true);
            setSize(storedSize);
        }
    }, [sizeRestored, getPageState, setSize, config.pageKey]);

    useEffect(() => {
        const current = getPageState(config.pageKey);
        setPageState(config.pageKey, { ...current, size });
    }, [size, getPageState, setPageState, config.pageKey]);

    const items = useMemo((): TItem[] => {
        if (!data) return [];
        const all = data.flatMap((page) => page.items ?? []);
        const seen = new Set<string>();
        return all.filter((item) => {
            if (seen.has(item.id)) return false;
            seen.add(item.id);
            return true;
        });
    }, [data]);

    const stats = statsData ?? { total: 0, unknown: 0, recognized: 0 };
    const totalCount = data?.[0]?.page?.totalItems ?? 0;

    const hasMore = (() => {
        if (!data || data.length === 0) return false;
        const lastPage = data[data.length - 1];
        if (!lastPage.page) return false;
        return lastPage.page.page < lastPage.page.totalPages;
    })();

    const isLoadingMore = isValidating && size > 1;
    const isInitialLoading = isLoading && items.length === 0;

    useScrollRestore({
        pageKey: config.pageKey,
        scrollContainer,
        isReady: !isInitialLoading,
    });

    const statusTabs = [
        {
            id: StatusFilter.ALL,
            label: config.tabLabels.all,
            count: stats.total,
        },
        {
            id: StatusFilter.RECOGNIZED,
            label: config.tabLabels.recognized,
            count: stats.recognized,
            disabled: stats.recognized === 0,
        },
        {
            id: StatusFilter.UNKNOWN,
            label: config.tabLabels.unknown,
            count: stats.unknown,
            disabled: stats.unknown === 0,
        },
    ];

    useEffect(() => {
        if (error || statsError || tagsError) {
            toast.error(config.messages.failedToLoad);
        }
    }, [error, statsError, tagsError, config.messages.failedToLoad]);

    const loadMore = useCallback(() => {
        if (hasMore && !isValidating) {
            void setSize(size + 1);
        }
    }, [hasMore, isValidating, setSize, size]);

    const selection = useGridMultiSelection();

    const { clearSelection } = selection;
    useEffect(() => {
        clearSelection();
    }, [cacheKey, clearSelection]);

    const editModal = useDirectoryEditModal<TItem>(items);

    const revalidateAll = async () => {
        await Promise.all([
            mutate(),
            mutateStats(),
            mutateTags(),
            revalidateByCacheKeyPrefix(config.cacheKeyPrefix),
        ]);
    };

    const hasNarrowingFilters =
        filters.searchQuery.length > 0 ||
        filters.selectedTags.length > 0 ||
        filters.selectedVehicleTypes.length > 0;

    const deletableTotal: number | undefined =
        filters.knownParam === false
            ? totalCount
            : filters.knownParam === undefined
              ? hasNarrowingFilters
                  ? undefined
                  : stats.unknown
              : 0;

    const describeSelection = () => ({
        selection: {
            selectAll: true,
            selectedAt:
                selection.selection.mode === 'all'
                    ? selection.selection.selectedAt
                    : Math.floor(Date.now() / 1000),
            excludeIds:
                selection.selection.mode === 'all'
                    ? Array.from(selection.selection.excluded)
                    : [],
        },
        filters: {
            known: false,
            search: filters.searchQuery || undefined,
            vehicleTypes: filters.selectedVehicleTypes.length
                ? filters.selectedVehicleTypes
                : undefined,
        } as Record<string, unknown>,
    });

    const deleteFlow = useDirectoryDeleteFlow({
        bulkDeleteItems: config.bulkDeleteItems,
        describeSelection,
        onSuccess: revalidateAll,
        onDeleteSuccess: config.onDeleteSuccess,
        onDeleteError: config.onDeleteError,
        onPartialDelete: config.onPartialDelete,
        onDeleteInProgress: config.onDeleteInProgress,
    });

    const handleItemClick = (itemId: string, event?: MouseEvent) => {
        if (selection.isMultiSelectMode) {
            selection.toggleSelected(itemId);
            return;
        }
        const route = config.itemRoute.replace(
            `:${config.routeParamKey}`,
            itemId,
        );
        openUrl(route, event, { preserveSearchParams: false });
    };

    const selectableItems = useMemo(
        () => items.filter((item) => config.canDeleteItem?.(item) ?? true),
        [items, config],
    );

    const handleSelectAll = () => {
        const selectableTotal = deletableTotal ?? selectableItems.length;
        if (
            selection.selectedCount > 0 &&
            selection.selectedCount === selectableTotal
        ) {
            selection.clearSelection();
            return;
        }
        if (!deletableTotal || !config.bulkDeleteItems) {
            selection.selectAll(selectableItems.map((item) => item.id));
            return;
        }
        selection.selectAllMatching({
            selectedAt: Math.floor(Date.now() / 1000),
            total: deletableTotal,
        });
    };
    const isItemSelected = (item: TItem) =>
        selection.isSelected(item as { id: string; createdAt?: number }) &&
        (config.canDeleteItem?.(item) ?? true);

    const requestDeleteSelected = () => {
        if (selection.isDescribed) {
            deleteFlow.requestDescribedBulkDelete();
            return;
        }
        const deletableIds = selectableItems
            .filter((item) => selection.selectedIds.has(item.id))
            .map((item) => item.id);
        deleteFlow.requestBulkDelete(deletableIds);
    };

    const bulkDeleteCount = (() => {
        const request = deleteFlow.bulkRequest;
        if (request === null) return 0;
        return 'described' in request
            ? selection.selectedCount
            : request.ids.length;
    })();

    const confirmDeleteSelected = async () => {
        try {
            await deleteFlow.handleBulkDeleteConfirm();
        } finally {
            selection.exitMultiSelectMode();
        }
    };

    const handleUpdate = async (itemId: string, payload: TPayload) => {
        setIsUpdating(true);
        try {
            await config.updateItem(itemId, payload);
            await revalidateAll();
            config.onUpdateSuccess?.(editModal.modalMode);
            editModal.setIsEditModalOpen(false);
            editModal.setItemToEdit(undefined);
            return true;
        } catch (error) {
            config.onUpdateError?.(editModal.modalMode, {
                itemId,
                payload,
                error,
            });
            return false;
        } finally {
            setIsUpdating(false);
        }
    };

    const markAsUnknown = async (itemId: string) => {
        const { payload, optimisticPatch } = config.markAsUnknownUpdate?.() ?? {
            payload: { known: false } as TPayload,
            optimisticPatch: { known: false } as unknown as Partial<TItem>,
        };

        const applyOptimisticPatch = (
            pages: PaginatedResponse<TItem>[] | undefined,
        ): PaginatedResponse<TItem>[] =>
            (pages ?? []).map((page) => ({
                ...page,
                items: page.items.map((item) =>
                    item.id === itemId
                        ? ({ ...item, ...optimisticPatch } as TItem)
                        : item,
                ),
            }));

        try {
            await mutate(
                async (current) => {
                    await config.updateItem(itemId, payload);
                    return applyOptimisticPatch(current);
                },
                {
                    optimisticData: applyOptimisticPatch,
                    rollbackOnError: true,
                },
            );
            await Promise.all([
                mutateStats(),
                mutateTags(),
                revalidateByCacheKeyPrefix(config.cacheKeyPrefix),
            ]);
            config.onMarkAsUnknownSuccess?.();
        } catch {
            config.onMarkAsUnknownError?.();
        }
    };

    const changeStatusFilter = useCallback(
        (value: StatusFilter) => {
            filters.setStatusFilter(value);
            resetPagination();
        },
        [filters.setStatusFilter, resetPagination],
    );

    const changeSearchQuery = useCallback(
        (value: string) => {
            filters.setSearchQuery(value);
            resetPagination();
        },
        [filters.setSearchQuery, resetPagination],
    );

    const changeSortOption = useCallback(
        (value: string) => {
            filters.setSortOption(value);
            resetPagination();
        },
        [filters.setSortOption, resetPagination],
    );

    const toggleTag = useCallback(
        (tag: string) => {
            filters.setSelectedTags((current) =>
                current.includes(tag)
                    ? current.filter((value) => value !== tag)
                    : [...current, tag],
            );
            resetPagination();
        },
        [filters.setSelectedTags, resetPagination],
    );

    const clearTags = useCallback(() => {
        filters.setSelectedTags([]);
        resetPagination();
    }, [filters.setSelectedTags, resetPagination]);

    const toggleVehicleType = useCallback(
        (vehicleType: string) => {
            filters.setSelectedVehicleTypes((current) =>
                current.includes(vehicleType)
                    ? current.filter((value) => value !== vehicleType)
                    : [...current, vehicleType],
            );
            resetPagination();
        },
        [filters.setSelectedVehicleTypes, resetPagination],
    );

    const clearVehicleTypes = useCallback(() => {
        filters.setSelectedVehicleTypes([]);
        resetPagination();
    }, [filters.setSelectedVehicleTypes, resetPagination]);

    return {
        items,
        stats,
        statusFilter: filters.statusFilter,
        setStatusFilter: changeStatusFilter,
        searchDraft: filters.searchDraft,
        setSearchDraft: filters.setSearchDraft,
        setSearchQuery: changeSearchQuery,
        sortOption: filters.sortOption,
        setSortOption: changeSortOption,
        selectedTags: filters.selectedTags,
        tagOptions,
        toggleTag,
        clearTags,
        selectedVehicleTypes: filters.selectedVehicleTypes,
        toggleVehicleType,
        clearVehicleTypes,
        statusTabs,
        hasMore,
        isLoadingMore,
        isInitialLoading,
        loadMore,
        scrollContainer,
        setScrollContainer,
        handleItemClick,
        handleSelectAll,
        requestDeleteSelected,
        confirmDeleteSelected,
        cancelDeleteSelected: deleteFlow.handleBulkDeleteCancel,
        bulkDeleteCount,
        selectableCount: selectableItems.length,
        totalCount: config.bulkDeleteItems ? deletableTotal : totalCount,
        isItemSelected,
        selection,
        isEditModalOpen: editModal.isEditModalOpen,
        modalMode: editModal.modalMode,
        itemToEdit: editModal.itemToEdit,
        isUpdating,
        openEditModal: editModal.openEditModal,
        closeEditModal: editModal.closeEditModal,
        handleUpdate,
        markAsUnknown,
        deleteConfirmId: deleteFlow.deleteConfirmId,
        isDeleting: deleteFlow.isDeleting,
        handleDeleteClick: deleteFlow.handleDeleteClick,
        handleDeleteConfirm: deleteFlow.handleDeleteConfirm,
        handleDeleteCancel: deleteFlow.handleDeleteCancel,
        revalidateAll,
    };
}
